import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  CreatePackagingOptionInput,
  PackagingOption,
  PatchPackagingOptionInput,
} from '@magimp/shared';
import {
  Button,
  DataTable,
  EditorModal,
  EmptyState,
  ErrorState,
  Input,
  LoadingState,
  Select,
  ToolbarActions,
} from '../../../ui/index.js';
import { CancelButton, EditorFooter, PackagingOptionForm } from './PackagingOptionForm.js';
import { parseSortOrderInput } from '../../../shared/index.js';
import {
  activatePackagingOption,
  archivePackagingOption,
  createPackagingOption,
  listPackagingOptions,
  patchPackagingOption,
} from '../../../api/packaging-options.js';
import { errorMessage } from '../../../api/errors.js';
import { formatDateTime } from '../../../i18n/index.js';
import { useToastStore } from '../../../stores/toast.js';
import { hasPermission, useAuthStore } from '../../../stores/auth.js';
import { PACKAGING_OPTIONS_ACTIVE_QK } from '../../../catalog/index.js';
import { PACKAGING_OPTIONS_CATALOG_QK } from '../queries.js';

type StatusFilter = 'active' | 'inactive' | 'all';

export function PackagingOptionsPage() {
  const { t } = useTranslation(['packagingOptions', 'common']);
  // WHY: route is gated on `packaging.manage`, but the defensive check
  //   matches the dictionary reference (ArrivalCitiesPage) so any
  //   future relaxation of the route gate cannot silently leak
  //   create/edit/archive affordances to read-only roles.
  const permissions = useAuthStore((s) => s.permissions);
  const canManage = hasPermission(permissions, 'packaging.manage');
  const [status, setStatus] = useState<StatusFilter>('active');
  const [q, setQ] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [selected, setSelected] = useState<PackagingOption | null>(null);

  const query = useQuery({
    queryKey: [...PACKAGING_OPTIONS_CATALOG_QK, status, q],
    queryFn: () => listForFilter(status, q),
  });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10, minHeight: 0, flex: 1 }}>
      <header style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <h1 style={{ fontSize: 20, fontWeight: 600, margin: 0, flex: 1 }}>
          {t('packagingOptions:title')}
        </h1>
        <ToolbarActions>
          <Input
            value={q}
            onChange={setQ}
            icon="search"
            placeholder={t('packagingOptions:search')}
            maxLength={200}
          />
          <Select
            value={status}
            onChange={(v) => setStatus(v as StatusFilter)}
            options={[
              { value: 'active', label: t('packagingOptions:filters.active') },
              { value: 'inactive', label: t('packagingOptions:filters.inactive') },
              { value: 'all', label: t('packagingOptions:filters.all') },
            ]}
            style={{ width: 150 }}
          />
          {canManage && (
            <Button kind="primary" icon="plus" onClick={() => setCreateOpen(true)}>
              {t('packagingOptions:actions.create')}
            </Button>
          )}
        </ToolbarActions>
      </header>
      {query.error && (
        <ErrorState
          title={t('common:states.loadError')}
          text={errorMessage(query.error)}
          onRetry={() => void query.refetch()}
        />
      )}
      {query.isPending && <LoadingState />}
      {query.isSuccess && (
        <DataTable
          rows={query.data}
          onSelect={setSelected}
          emptyState={
            <EmptyState title={t('common:states.emptyTitle')} text={t('packagingOptions:empty')} />
          }
          columns={[
            { key: 'name', label: t('packagingOptions:columns.name'), sortable: true },
            {
              key: 'sortOrder',
              label: t('packagingOptions:columns.sortOrder'),
              width: 110,
              sortable: true,
              sortValue: (row) => row.sortOrder,
              render: (row) => (
                <span style={{ fontVariantNumeric: 'tabular-nums' }}>{row.sortOrder}</span>
              ),
            },
            {
              key: 'status',
              label: t('packagingOptions:columns.status'),
              width: 130,
              sortable: true,
              sortValue: (row) => (row.active ? 1 : 0),
              render: (row) =>
                row.active
                  ? t('packagingOptions:status.active')
                  : t('packagingOptions:status.inactive'),
            },
            {
              key: 'createdAt',
              label: t('packagingOptions:columns.createdAt'),
              width: 170,
              sortable: true,
              sortValue: (row) => row.createdAt,
              render: (row) => formatDateTime(row.createdAt),
            },
          ]}
        />
      )}
      {createOpen && canManage && (
        <PackagingOptionCreateModal onClose={() => setCreateOpen(false)} />
      )}
      {selected && (
        <PackagingOptionEditorModal
          row={selected}
          canManage={canManage}
          onClose={() => setSelected(null)}
          onUpdated={setSelected}
        />
      )}
    </div>
  );
}

async function listForFilter(status: StatusFilter, q: string): Promise<PackagingOption[]> {
  const base: { q?: string; limit: number } = { limit: 500 };
  if (q.trim()) base.q = q.trim();
  if (status === 'active') return (await listPackagingOptions({ ...base, active: true })).items;
  if (status === 'inactive') return (await listPackagingOptions({ ...base, active: false })).items;
  // WHY (#1152 Codex F-001): `all` MUST preserve backend
  //   `(sortOrder ASC, id ASC)` order — see RoadOptionsPage.
  return (await listPackagingOptions({ ...base })).items;
}

function invalidatePackagingCaches(qc: ReturnType<typeof useQueryClient>): void {
  void qc.invalidateQueries({ queryKey: PACKAGING_OPTIONS_CATALOG_QK });
  void qc.invalidateQueries({ queryKey: PACKAGING_OPTIONS_ACTIVE_QK });
}

function PackagingOptionCreateModal({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation(['packagingOptions', 'common']);
  const qc = useQueryClient();
  const showToast = useToastStore((s) => s.show);
  const [name, setName] = useState('');
  const [sortOrder, setSortOrder] = useState('0');

  const payload: CreatePackagingOptionInput | null = useMemo(() => {
    const trimmed = name.trim();
    if (!trimmed) return null;
    const parsed = parseSortOrderInput(sortOrder);
    if (parsed === null) return null;
    return { name: trimmed, sortOrder: parsed, active: true };
  }, [name, sortOrder]);

  const mutation = useMutation({
    mutationFn: createPackagingOption,
    onSuccess: () => {
      setName('');
      setSortOrder('0');
      invalidatePackagingCaches(qc);
      showToast({ tone: 'success', text: t('packagingOptions:toasts.created') });
      onClose();
    },
    onError: (err) => showToast({ tone: 'error', text: errorMessage(err) }),
  });

  const dirty = name.trim().length > 0 || sortOrder.trim() !== '0';

  return (
    <EditorModal
      open
      title={t('packagingOptions:modals.createTitle')}
      width={420}
      dirty={dirty}
      busy={mutation.isPending}
      onConfirmClose={onClose}
    >
      <PackagingOptionForm
        name={name}
        sortOrder={sortOrder}
        active
        onChangeName={setName}
        onChangeSortOrder={setSortOrder}
      />
      <EditorFooter>
        <CancelButton onClose={onClose} label={t('common:actions.cancel')} />
        <Button
          kind="primary"
          disabled={payload === null || mutation.isPending}
          onClick={() => payload && mutation.mutate(payload)}
        >
          {t('common:actions.create')}
        </Button>
      </EditorFooter>
    </EditorModal>
  );
}

function PackagingOptionEditorModal({
  row,
  canManage,
  onClose,
  onUpdated,
}: {
  row: PackagingOption;
  canManage: boolean;
  onClose: () => void;
  onUpdated: (row: PackagingOption) => void;
}) {
  const { t } = useTranslation(['packagingOptions', 'common']);
  const qc = useQueryClient();
  const showToast = useToastStore((s) => s.show);
  const [name, setName] = useState(row.name);
  const [sortOrder, setSortOrder] = useState(String(row.sortOrder));

  const patch: PatchPackagingOptionInput | null = useMemo(() => {
    const trimmed = name.trim();
    const diff: PatchPackagingOptionInput = {};
    if (trimmed && trimmed !== row.name) {
      diff.name = trimmed;
    } else if (!trimmed) {
      return null;
    }
    const parsed = parseSortOrderInput(sortOrder);
    if (parsed === null) return null;
    if (parsed !== row.sortOrder) {
      diff.sortOrder = parsed;
    }
    return Object.keys(diff).length === 0 ? null : diff;
  }, [name, sortOrder, row.name, row.sortOrder]);

  const patchMutation = useMutation({
    mutationFn: (body: PatchPackagingOptionInput) => patchPackagingOption(row.id, body),
    onSuccess: (next) => {
      setName(next.name);
      setSortOrder(String(next.sortOrder));
      invalidatePackagingCaches(qc);
      showToast({ tone: 'success', text: t('packagingOptions:toasts.updated') });
      onUpdated(next);
    },
    onError: (err) => showToast({ tone: 'error', text: errorMessage(err) }),
  });

  const archiveMutation = useMutation({
    mutationFn: () => archivePackagingOption(row.id),
    onSuccess: (next) => {
      invalidatePackagingCaches(qc);
      showToast({ tone: 'success', text: t('packagingOptions:toasts.archived') });
      onUpdated(next);
    },
    onError: (err) => showToast({ tone: 'error', text: errorMessage(err) }),
  });

  const activateMutation = useMutation({
    mutationFn: () => activatePackagingOption(row.id),
    onSuccess: (next) => {
      invalidatePackagingCaches(qc);
      showToast({ tone: 'success', text: t('packagingOptions:toasts.activated') });
      onUpdated(next);
    },
    onError: (err) => showToast({ tone: 'error', text: errorMessage(err) }),
  });

  const busy = patchMutation.isPending || archiveMutation.isPending || activateMutation.isPending;
  const dirty = patch !== null;

  return (
    <EditorModal
      open
      title={t('packagingOptions:modals.editTitle', { name: row.name })}
      width={420}
      dirty={dirty}
      busy={busy}
      onConfirmClose={onClose}
    >
      <PackagingOptionForm
        name={name}
        sortOrder={sortOrder}
        active={row.active}
        readOnly={!canManage}
        onChangeName={setName}
        onChangeSortOrder={setSortOrder}
      />
      <EditorFooter>
        {canManage &&
          (row.active ? (
            <Button kind="ghost" disabled={busy} onClick={() => archiveMutation.mutate()}>
              {t('common:actions.archive')}
            </Button>
          ) : (
            <Button kind="ghost" disabled={busy} onClick={() => activateMutation.mutate()}>
              {t('common:actions.restore')}
            </Button>
          ))}
        <CancelButton onClose={onClose} label={t('common:actions.close')} />
        {canManage && (
          <Button
            kind="primary"
            disabled={patch === null || busy}
            onClick={() => patch && patchMutation.mutate(patch)}
          >
            {t('common:actions.save')}
          </Button>
        )}
      </EditorFooter>
    </EditorModal>
  );
}
